// Card validation consumes canonical recipe data, never generated table cells.
import { outputQuantityBase, resolveUsableOutput } from './recipeScaling';

export const isPadiIssue = item => /^padi/.test(item.code || '') || /\bPADI\b|process relationships|regenerate and review the process/i.test(item.label || item);

// This is only a navigation decision. The backend remains authoritative when
// a normal Workspace job is created from a frozen recipe snapshot.
export const padiExecutionUnavailable = recipe => !recipe ||
  (recipe.padi_questions || []).length > 0 ||
  (recipe.recipe_readiness?.missing || []).some(isPadiIssue) ||
  (!(recipe.padi_grid?.cells || []).length && !(recipe.qpt_flow?.stages || []).length);

export function recipeAttention(recipe) {
  const issues = [...(recipe.recipe_readiness?.missing || [])];
  for (const question of recipe.padi_questions || []) {
    const code = question.id ? `padi_process_${question.id}` : 'padi';
    if (!issues.some(item => question.id && item.code === code)) issues.push({ code, label: question.question });
  }
  if (!recipe.padi_grid && !recipe.qpt_flow?.stages?.length && !issues.some(isPadiIssue)) issues.push({ code: 'padi', label: 'PADI not generated' });
  const identity = item => item.code?.startsWith('padi_process_') ? item.code : `${item.code || ''}:${item.label}`;
  return issues.filter((item, index) => item.label && issues.findIndex(other => identity(other) === identity(item)) === index);
}

export function recipeInstructions(recipe) {
  if (String(recipe.sop || '').trim()) {
    // Retain sentences, wording and order; split only explicit numbered steps
    // or lines. This is deliberately not process interpretation.
    return recipe.sop.split(/\r?\n|(?<=\S)\s+(?=\d+[.)]\s+[A-Z])/).map(line => line.trim()).filter(Boolean);
  }
  return (recipe.steps || []).map(step => step.detailed_text).filter(Boolean);
}

export function recipeCardBlockers(recipe) {
  const issues = [];
  const padiMessages = new Set([...(recipe.padi_questions || []).map(item => item.question), ...(recipe.recipe_readiness?.missing || []).filter(isPadiIssue).map(item => item.label)]);
  const components = recipe.components || [];
  if (!components.length) issues.push('Add recipe components before use.');
  components.forEach((component, index) => {
    const name = component.ingredient_name || component.prep_recipe_name || `Component ${index + 1}`;
    if (!component.component_id || !(Number(component.quantity) > 0) || !Number.isFinite(Number(component.quantity)) || !String(component.unit || '').trim()) issues.push(`${name}: a valid reference, quantity and unit are required.`);
  });
  const usable = resolveUsableOutput({ quantity: recipe.yield_quantity, unit: recipe.yield_unit }, recipe.output_variants || [], recipe.usable_output_quantity != null ? { quantity: recipe.usable_output_quantity, unit: recipe.usable_output_unit || recipe.yield_unit } : null);
  if (recipe.usable_output_quantity != null && (!outputQuantityBase(recipe.usable_output_quantity, recipe.usable_output_unit || recipe.yield_unit) || outputQuantityBase(recipe.usable_output_quantity, recipe.usable_output_unit || recipe.yield_unit)?.dimension !== outputQuantityBase(recipe.yield_quantity, recipe.yield_unit)?.dimension)) issues.push('Usable output must have a positive quantity and a compatible unit.');
  if (!outputQuantityBase(usable?.quantity, usable?.unit)) issues.push('Valid output quantity and unit are required for scaling.');
  if (usable?.error) issues.push(usable.error);
  for (const item of recipe.recipe_readiness?.missing || []) {
    if (!isPadiIssue(item) && /missing ingredient|missing prep item|no longer exists|conversion|incomplete.*cost/i.test(item.label || '')) issues.push(item.label);
  }
  for (const message of [...(recipe.approval_blockers || []), ...(recipe.costing_error ? recipe.costing_error.split('; ') : [])]) {
    if (!isPadiIssue(message) && !padiMessages.has(message)) issues.push(message);
  }
  return [...new Set(issues)];
}
